// A weekly or monthly activity card (docs/design/social-plan.md 3, "Activity"). Built only from the
// kit: a Card with the counts as Stats, the wine types, regions, countries and grapes as Tags (new
// ones marked in words, not by tint alone), the blind calls and the badges earned. A card holds
// lexicon codes and numbers only, so every name here comes from the domain's lists; nothing is
// free text. Under someone else's card sit the reaction chips; under your own, who reacted.
import type { CardView, ReactionKind } from "@proef/contract";
import { REACTION_KINDS, localizedCountryName, type CardContent } from "@proef/domain";
import { mk, useT } from "../lib/i18n";
import {
  badgeLabelKey,
  grapeLabel,
  periodLabel,
  regionLabel,
  weekNumber,
} from "../lib/social/cardLabels";
import {
  Avatar,
  Body,
  Caption,
  Card,
  Chip,
  Chips,
  Column,
  ErrorBanner,
  Eyebrow,
  Row,
  Stat,
  StatRow,
  Tag,
} from "../ui";
import { initialsOf } from "./PersonRow";

interface Tally {
  readonly code: string;
  readonly count: number;
  readonly isNew: boolean;
}

function TallyChips({
  title,
  tallies,
  label,
}: {
  title: string;
  tallies: readonly Tally[];
  label: (code: string) => string;
}): React.JSX.Element | null {
  const { t } = useT();
  if (tallies.length === 0) return null;
  return (
    <Column gap="2">
      <Eyebrow>{title}</Eyebrow>
      <Chips>
        {tallies.map((entry) => (
          <Tag
            key={entry.code}
            label={
              entry.isNew ? `${label(entry.code)} · ${t("social.card.new")}` : label(entry.code)
            }
            count={entry.count}
            solid={entry.isNew}
          />
        ))}
      </Chips>
    </Column>
  );
}

/** What a card says, without who it is from: the same body for a preview and for a shared card. */
export function CardSummary({ content }: { content: CardContent }): React.JSX.Element {
  const { t, locale } = useT();
  const { counts } = content;
  return (
    <Column gap="4">
      <StatRow>
        <Stat value={counts.wines} label={t("social.card.stat.wines")} />
        <Stat value={counts.notes} label={t("social.card.stat.notes")} />
        <Stat value={counts.sessions} label={t("social.card.stat.sessions")} />
        {content.streakWeeks > 0 && (
          <Stat value={content.streakWeeks} label={t("social.card.stat.streak")} />
        )}
      </StatRow>
      {content.wineTypes.length > 0 && (
        <Column gap="2">
          <Eyebrow>{t("social.card.types")}</Eyebrow>
          <Chips>
            {content.wineTypes.map((entry) => (
              <Tag
                key={entry.type}
                label={t(mk(`social.card.type.${entry.type}`))}
                count={entry.count}
              />
            ))}
          </Chips>
        </Column>
      )}
      <TallyChips title={t("social.card.regions")} tallies={content.regions} label={regionLabel} />
      <TallyChips
        title={t("social.card.countries")}
        tallies={content.countries}
        label={(code) => localizedCountryName(code, locale)}
      />
      <TallyChips title={t("social.card.grapes")} tallies={content.grapes} label={grapeLabel} />
      {content.blind.calls > 0 && (
        <Body>
          {t("social.card.blind", { correct: content.blind.correct, calls: content.blind.calls })}
        </Body>
      )}
      {content.badges.length > 0 && (
        <Column gap="2">
          <Eyebrow>{t("social.card.badges")}</Eyebrow>
          <Chips>
            {content.badges.map((id) => (
              <Tag key={id} solid label={t(mk(badgeLabelKey(id)))} />
            ))}
          </Chips>
        </Column>
      )}
    </Column>
  );
}

export interface ActivityCardProps {
  card: CardView;
  /** Called with the reaction picked, or null to take back the one given. Absent on your own card. */
  onReact?: ((kind: ReactionKind | null) => void) | undefined;
  /** A reaction is on its way; the chips wait. */
  reacting?: boolean | undefined;
  error?: string | undefined;
  testId?: string | undefined;
}

export function ActivityCard({
  card,
  onReact,
  reacting = false,
  error,
  testId,
}: ActivityCardProps): React.JSX.Element {
  const { t, locale } = useT();
  const audience = card.audience;
  const own = audience !== null;
  // A preview of your own card nobody sees yet has no reactions to show.
  const shared = audience !== null && (audience.friends || audience.groupIds.length > 0);
  const range = periodLabel(card.period, card.periodKey, locale);
  const weekTitle = t("social.card.title.week", { n: weekNumber(card.periodKey) });
  const when = card.period === "week" ? [weekTitle, range].join(" · ") : range;
  const title = own
    ? card.period === "week"
      ? weekTitle
      : range
    : t(card.period === "week" ? "social.activity.card.week" : "social.activity.card.month", {
        name: card.user.displayName,
      });
  const subtitle = own ? (card.period === "week" ? range : undefined) : when;
  const given = REACTION_KINDS.filter((kind) => (card.reactions[kind] ?? 0) > 0);

  return (
    <Card data-testid={testId} gap="4">
      <Row gap="3" alignItems="center">
        <Avatar
          initials={initialsOf(card.user.displayName)}
          src={card.user.avatarUrl ?? undefined}
          size="inline"
        />
        <Column flex={1} minWidth={0}>
          <Body fontWeight="600">{title}</Body>
          {subtitle !== undefined && <Caption>{subtitle}</Caption>}
        </Column>
      </Row>
      <CardSummary content={card.content} />
      {onReact !== undefined && !own ? (
        <Row
          gap="2"
          flexWrap="wrap"
          role="group"
          aria-label={t("social.reaction.group", { name: card.user.displayName })}
        >
          {REACTION_KINDS.map((kind) => {
            return (
              <Chip
                key={kind}
                label={t(mk(`social.reaction.${kind}`))}
                active={card.myReaction === kind}
                disabled={reacting}
                count={card.reactions[kind]}
                onPress={() => {
                  onReact(card.myReaction === kind ? null : kind);
                }}
              />
            );
          })}
        </Row>
      ) : shared ? (
        <Column gap="2">
          <Eyebrow>{t("social.reaction.own")}</Eyebrow>
          {given.length === 0 ? (
            <Caption>{t("social.reaction.none")}</Caption>
          ) : (
            <Chips>
              {given.map((kind) => (
                <Tag
                  key={kind}
                  label={t(mk(`social.reaction.${kind}`))}
                  count={card.reactions[kind] ?? 0}
                />
              ))}
            </Chips>
          )}
        </Column>
      ) : null}
      {error !== undefined && <ErrorBanner message={error} />}
    </Card>
  );
}
